import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const myQueueRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/my-queue',
  component: MyQueuePage,
});

function relativeDue(dueAt: string | null): string | null {
  if (!dueAt) return null;
  const ms = new Date(dueAt).getTime() - Date.now();
  const days = Math.round(ms / (24 * 60 * 60 * 1000));
  if (days > 1) return `due in ${days} days`;
  if (days === 1) return 'due tomorrow';
  if (days === 0) return 'due today';
  if (days === -1) return 'overdue by 1 day';
  return `overdue by ${-days} days`;
}

function MyQueuePage(): JSX.Element {
  const queueQuery = useQuery({
    queryKey: ['queue-items'],
    queryFn: () => api.listQueueItems(),
  });

  const items = queueQuery.data?.items ?? [];
  const byKind = queueQuery.data?.byKind ?? {};
  const total = queueQuery.data?.total ?? 0;
  const labelOf = new Map(items.map((it) => [it.kind, it.label]));

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          My queue
        </h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Open items assigned to you across ledger, ideas, leaves, initiatives, and vault. Items
          close only when the underlying action is resolved.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{total} open item{total === 1 ? '' : 's'}</CardTitle>
          <CardDescription className="break-words">
            {Object.entries(byKind)
              .map(([k, n]) => `${labelOf.get(k) ?? k} × ${n}`)
              .join(' · ') || 'nothing pending — inbox zero'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              Nothing pending — inbox zero.
            </p>
          ) : (
            <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
              {items.map((it) => {
                const due = relativeDue(it.dueAt);
                const overdue = due?.startsWith('overdue');
                return (
                  <li
                    key={it.id}
                    className="flex flex-col items-start gap-2 py-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                  >
                    <div className="min-w-0 max-w-full">
                      <div className="font-medium">
                        <Link to={it.deepLink as never}>{it.label}</Link>
                      </div>
                      <div className="text-xs text-[hsl(var(--muted-foreground))]">
                        Opened {new Date(it.createdAt).toISOString().slice(0, 10)}
                      </div>
                    </div>
                    {due ? (
                      <Badge
                        variant={overdue ? 'destructive' : 'muted'}
                        className="shrink-0 whitespace-nowrap"
                      >
                        {due}
                      </Badge>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
